import { Button, Result, Typography } from 'antd'
import { Component, type ErrorInfo, type ReactNode } from 'react'

const { Paragraph, Text } = Typography

interface Props {
  children: ReactNode
  /**
   * Optional fallback. When omitted, the default Arabic Result UI is shown.
   * Receives the captured error and a `reset` callback that clears the
   * boundary state so the tree can mount again.
   */
  fallback?: (args: { error: Error; reset: () => void }) => ReactNode
  /**
   * Hook for sending the error elsewhere (e.g. Sentry). Called once per
   * thrown error. The boundary still renders its fallback regardless.
   */
  onError?: (error: Error, info: ErrorInfo) => void
}

interface State {
  error: Error | null
}

/**
 * App-wide error boundary. Catches synchronous render errors in the React
 * tree and renders an Arabic fallback page with "reload" and "back to home"
 * actions. Use one global instance around <RouterProvider /> and additional
 * local instances inside feature routes if you want finer-grained recovery.
 *
 * Async errors (Promise rejections, event handlers) are NOT caught here —
 * those bubble through the QueryClient / unhandledrejection path instead.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.props.onError?.(error, info)
    // Surface in dev console regardless of onError so it's never silent.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info)
  }

  reset = () => {
    this.setState({ error: null })
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    if (this.props.fallback) {
      return this.props.fallback({ error, reset: this.reset })
    }

    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          background: 'var(--aufq-bg-1, #FBF9F4)',
        }}
      >
        <Result
          status="error"
          title="حدث خطأ غير متوقع"
          subTitle="نأسف للإزعاج. حدث خطأ في عرض الصفحة. يمكنك إعادة المحاولة أو العودة للرئيسية."
          extra={[
            <Button key="reload" type="primary" onClick={() => window.location.reload()}>
              إعادة تحميل الصفحة
            </Button>,
            <Button key="home" onClick={() => (window.location.href = '/')}>
              العودة للرئيسية
            </Button>,
            <Button key="reset" type="link" onClick={this.reset}>
              المحاولة مرة أخرى
            </Button>,
          ]}
        >
          {import.meta.env.DEV ? (
            <Paragraph>
              <Text strong>تفاصيل الخطأ (وضع التطوير فقط):</Text>
              <pre
                style={{
                  textAlign: 'left',
                  direction: 'ltr',
                  background: 'rgba(0,0,0,0.04)',
                  padding: 12,
                  borderRadius: 8,
                  fontSize: 12,
                  marginTop: 8,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: 240,
                  overflow: 'auto',
                }}
              >
                {error.stack ?? error.message}
              </pre>
            </Paragraph>
          ) : null}
        </Result>
      </div>
    )
  }
}

export default ErrorBoundary
